import { Injectable, inject } from '@angular/core';

import { Observable, ReplaySubject, catchError, of, switchMap, tap } from 'rxjs';

import { EnvironmentService, PlatformService, StorageService } from '@hdms/shared/util-core';

import { BannerHelperService } from './banner-helper.service';
import { CustomerJourneyGqlService } from './customer-journey-gql.service';

// prefixed with 'pf' so page-frame StorageService instance will clean it up on logout
const CUSTOMER_JOURNEY_STORAGE_KEY = 'pf-customerJourneyId';

@Injectable({
  providedIn: 'root'
})
export class CustomerJourneyService {
  private readonly storageService = inject(StorageService);
  private readonly platform = inject(PlatformService);
  private readonly isMobileApp = inject(EnvironmentService).mobileApp;
  private readonly helper = inject(BannerHelperService);
  private readonly journeyGql = inject(CustomerJourneyGqlService);

  private readonly update = new ReplaySubject<boolean>(1);

  constructor() {
    (this.helper.megaMenuProductBannersEnabled ||
      this.helper.gridPageProductBannersEnabled ||
      this.helper.marketingBannersEnabled) &&
      this.listenToRouteChange();
  }

  get update$(): Observable<boolean> {
    return this.update.asObservable();
  }

  get guid(): string {
    return this.storageService.getItem(CUSTOMER_JOURNEY_STORAGE_KEY);
  }

  set guid(id: string) {
    this.storageService.setItem(CUSTOMER_JOURNEY_STORAGE_KEY, id);
  }

  public removeGuid(): void {
    this.storageService.removeItem(CUSTOMER_JOURNEY_STORAGE_KEY);
  }

  /**
   * Save customer journey on every NavigationEnd event, client-side only.
   * As we've to take care for late subscribers, update has to be a ReplaySubject.
   * So in order to block immediate banner loading after subscription while a
   * journey save is in progress, emit `false` before, so subscribers can filter,
   * then `true` once save request is done.
   * @private
   */
  private listenToRouteChange(): void {
    this.platform.isBrowser && !this.isMobileApp
      ? this.helper.routeChange$
          .pipe(
            tap(() => this.update.next(false)),
            switchMap(url => this.saveCustomerJourney$(url)),
            tap(() => this.update.next(true))
          )
          .subscribe()
      : this.update.next(true);
  }

  /**
   * Save customer journey and update journey GUID in session storage.
   * @param uri
   * @private
   */
  private saveCustomerJourney$(uri: string): Observable<unknown> {
    return this.journeyGql
      .mutate({
        input: {
          uri,
          journeyId: this.guid,
          productId: this.helper.productId
        }
      })
      .pipe(
        tap(({ data }) => (this.guid = data.saveCustomerJourney.journeyId)),
        catchError(() => {
          this.removeGuid();
          return of(null);
        })
      );
  }
}
